/**
 * Model Capabilities Configuration
 * 
 * This file defines which AI models support specific capabilities like thinking mode.
 * It serves as a centralized configuration for feature availability across different models.
 */

/**
 * Models that support thinking mode functionality
 * 
 * Thinking mode enables deeper reasoning and step-by-step analysis for complex queries,
 * but results in slower response times due to enhanced processing.
 * 
 * Current status:
 * - glm-4.5: ✅ Fully implemented and tested
 * 
 * Note: Multi-model thinking architecture is preserved but commented out for clean UI.
 * Uncomment models below when ready for production deployment.
 */
export const THINKING_CAPABLE_MODELS = [
  'glm-4.5',           // ✅ Currently active - GLM-4.5 thinking mode
  // Future thinking-capable models (architecture ready, UI hidden):
  // 'o1-preview',     // 🔄 Commented out - OpenAI O1 Preview  
  // 'o1-mini',        // 🔄 Commented out - OpenAI O1 Mini
  // 'o3-mini',        // 🔄 Commented out - OpenAI O3 Mini
  // 'claude-3-5-sonnet', // 🔄 Commented out - Anthropic Claude 3.5 Sonnet
  // 'deepseek-r1:32b'    // 🔄 Commented out - DeepSeek R1 reasoning model
] as const;

/**
 * Type for thinking-capable model identifiers
 */
export type ThinkingCapableModel = typeof THINKING_CAPABLE_MODELS[number];

/**
 * Checks if a given model supports thinking mode
 * 
 * @param model - The model identifier to check
 * @returns True if the model supports thinking mode, false otherwise
 * 
 * @example
 * ```typescript
 * isThinkingCapable('glm-4.5') // returns true
 * isThinkingCapable('gpt-4') // returns false
 * ```
 */
export const isThinkingCapable = (model: string): model is ThinkingCapableModel => {
  return THINKING_CAPABLE_MODELS.includes(model as ThinkingCapableModel);
};

/**
 * Gets user-friendly display information for thinking mode capability
 * 
 * @param model - The model identifier to check
 * @returns Object with capability status and description
 */
export const getThinkingCapabilityInfo = (model: string) => {
  const capable = isThinkingCapable(model);
  
  return {
    capable,
    description: capable 
      ? 'Enable deeper reasoning for complex queries'
      : 'Thinking mode not available for this model',
    tooltip: capable
      ? 'Slower responses with enhanced reasoning and step-by-step analysis'
      : `Thinking mode is only available for: ${THINKING_CAPABLE_MODELS.join(', ')}`
  };
};

/**
 * Model capability indicators for UI display
 */
export const MODEL_CAPABILITY_INDICATORS = {
  THINKING: '' // Brain/thinking emoji for visual indication
  // THINKING: '💭' // Brain/thinking emoji for visual indication
} as const;